import { useState, createContext, useEffect } from 'react'
import { LegalEntityParty, MoneyAmount, MoneyAmountCurrencyCodeEnum } from '@types'
import { saveFormData, getFormData } from 'utils/localStorage'

type PointOfContact = {
  position?: string
  person?: {
    firstName: string
    lastName: string
    phone: string
    email: string
  }
}

type BeneficiaryEntry = LegalEntityParty & {
  onboarded: boolean // force onboarded to be required
} & PointOfContact

type GlobalStateType = {
  jwt: string
  isRequestSubmitted: boolean
  initiatedBy: string
  applicants: LegalEntityParty[]
  beneficiaries: BeneficiaryEntry[]
  issuers: LegalEntityParty[]
  termsIdentifier?: string
  guaranteeType: string
  amount: MoneyAmount
  reference: string | null
  expires: {
    date: {
      DD: string
      MM: string
      YYYY: string
      never: boolean
    }
  }
  expiryDate: string
  isOpenEnded: boolean
  type: string
  id: string
  hasNrbAdded: boolean
  purpose: {
    purposeDescription: string
    contract: {
      startDate: string | null
      endDate: string | null
      number: string | null
      name: string | null
    }
    property: {
      name: string | null
      unit: string | null
      street: string | null
      suburb: string | null
      postcode: string | null
      state: string | null
      country: string | null
    }
  }
  purposeTypeDescription: string
  terms: {
    termsIdentifier: string
    termsDisplay: string
    deedDisplay: string
    deedIdentifier: string
  }

  isThemeChanged: boolean
  setRequestSubmitted: (value: boolean) => void
  setReqInfo: (value: Object) => void
}

export const initialState: GlobalStateType = {
  jwt: '',
  isRequestSubmitted: true,
  initiatedBy: '',
  guaranteeType: 'digital',
  applicants: [],
  beneficiaries: [
    {
      legalEntityIdentifier: '',
      onboarded: true,
      commonName: '',
      contactFirstName: '',
      contactLastName: '',
      email: '',
      legalEntityCodes: [{ legalEntityCodeData: '', legalEntityCodeType: 'ABN' }],
      designatedPointOfContact: {
        position: '',
        person: {
          firstName: '',
          lastName: '',
          phone: '',
          email: ''
        }
      },
      requestedTrustee: undefined,
      legalEntityType: '',
      handlingInstructions: undefined
    }
  ],
  issuers: [],
  hasNrbAdded: false,
  termsIdentifier: '',
  purposeTypeDescription: '',
  amount: {
    units: 0,
    nanos: 0,
    currencyCode: MoneyAmountCurrencyCodeEnum.AUD
  },
  reference: '',
  expires: {
    date: { DD: '', MM: '', YYYY: '', never: false }
  },
  expiryDate: '',
  isOpenEnded: false,
  type: '',
  id: '',
  purpose: {
    purposeDescription: '',
    contract: {
      startDate: '',
      endDate: '',
      number: null,
      name: null
    },
    property: {
      name: '',
      unit: '',
      street: '',
      suburb: '',
      postcode: '',
      state: '',
      country: ''
    }
  },
  terms: {
    termsIdentifier: '',
    termsDisplay: '',
    deedDisplay: '',
    deedIdentifier: ''
  },
  isThemeChanged: false,
  setRequestSubmitted: () => {},
  setReqInfo: () => {}
}

const GlobalInfoContext = createContext(initialState)
GlobalInfoContext.displayName = 'InfoContext'

const GlobalInfoContextProvider = (props) => {
  const [info, setInfo] = useState(() => {
    // Try to restore from localStorage on initial load
    const savedInfo = getFormData()
    return savedInfo || initialState
  })

  const setReqInfo = (_mutatedProps) => {
    setInfo((prevInfo) => {
      const updatedInfo = { ...prevInfo, ..._mutatedProps }
      // Persist to localStorage whenever info changes
      saveFormData(updatedInfo)
      return updatedInfo
    })
  }
  
  const setRequestSubmitted = (value: boolean) => {
    setInfo((prevInfo) => {
      const updatedInfo = { ...prevInfo, ...{ isRequestSubmitted: value } }
      // Persist to localStorage
      saveFormData(updatedInfo)
      return updatedInfo
    })
  }

  useEffect(() => {
    // Placeholder for side effects if needed later
  }, [])

  return (
    <GlobalInfoContext.Provider value={{ ...info, setRequestSubmitted, setReqInfo }}>
      {props.children}
    </GlobalInfoContext.Provider>
  )
}

export { GlobalInfoContextProvider, GlobalInfoContext }
